/**
 * Everything known about one selection, in tabs.
 *
 * The funnel's results (which filter dropped what), the composed palette, and
 * the Palette tab where the palette is drawn in the shape it is marked and the
 * palette marking file is configured - patch size, fiducial crosses and where
 * the block sits on the plate - and downloaded.
 *
 * The page reads one overview answer from the server rather than assembling the
 * funnel itself, so what is shown here and what the marking file contains
 * cannot drift apart.
 */
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
    Stack, Typography, Box, Alert, Button, Card, CardContent, Table, TableBody,
    TableCell, TableHead, TableRow, Chip, Dialog, DialogTitle, DialogContent,
    Tabs, Tab, FormControlLabel, Switch, ToggleButton, ToggleButtonGroup, CircularProgress,
    TextField, Checkbox, Tooltip as MuiTooltip,
} from "@mui/material";
import {
    ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip, Cell,
    BarChart, Bar,
} from "recharts";
import DownloadIcon from "@mui/icons-material/Download";
import axios from "axios";
import NavBarBottom from "../../components/NavBarBottom";
import ExplorationPageShell from "../../components/ExplorationPageShell";
import MyScatterChart from "../../components/MyScatterChart";
import LaserMarkPanel from "../../components/LaserMarkPanel";
import MarkingTimeChip from "../../components/MarkingTimeChip";
import PalettePlacement from "../../components/PalettePlacement";
import LoadingComponent from "../../components/LoadingComponent";
import RepeatabilityGamutPlot from "../../components/RepeatabilityGamutPlot";
import RepeatabilityPhotoStrip from "../../components/RepeatabilityPhotoStrip";
import MeasurementDriftPlot from "../../components/MeasurementDriftPlot";
import LabGamutPlot from "../../components/LabGamutPlot";
import SampleOriginCard from "../../components/SampleOriginCard";
import { modeLabel } from "../../helper/repeatabilityText";
import RepeatabilityGrid from "../../components/RepeatabilityGrid";
import ModeContrastPairPlot from "../../components/ModeContrastPairPlot";
import ModeContrastResultTable from "../../components/ModeContrastResultTable";
import ModeContrastGroups, { directionKey, directionLabel, directionLimitsText, selectedText } from "../../components/ModeContrastGroups";
import PaletteSampleTooltip, { overviewSampleEntry } from "../../components/PaletteSampleTooltip";
import PaletteAsMarked, { paletteShapeText } from "../../components/PaletteAsMarked";
import {
    selectionSidebar, selectionStatusPresentation, selectionKind, selectionKindKey, isModeContrastSelection,
} from "../../helper/selectionFlow";
import { useSelectionParams } from "../../helper/entityRoutes";
import { downloadBlob } from "../../helper/markingPreviewWindow";

function labToRgb255(l, a, b) {
    let y = (l + 16) / 116;
    let x = a / 500 + y;
    let z = y - b / 200;
    x = 0.95047 * ((x * x * x > 0.008856) ? x * x * x : (x - 16 / 116) / 7.787);
    y = 1.00000 * ((y * y * y > 0.008856) ? y * y * y : (y - 16 / 116) / 7.787);
    z = 1.08883 * ((z * z * z > 0.008856) ? z * z * z : (z - 16 / 116) / 7.787);
    let r = x * 3.2406 + y * -1.5372 + z * -0.4986;
    let g = x * -0.9689 + y * 1.8758 + z * 0.0415;
    let bl = x * 0.0557 + y * -0.2040 + z * 1.0570;
    r = r > 0.0031308 ? 1.055 * Math.pow(r, 1 / 2.4) - 0.055 : 12.92 * r;
    g = g > 0.0031308 ? 1.055 * Math.pow(g, 1 / 2.4) - 0.055 : 12.92 * g;
    bl = bl > 0.0031308 ? 1.055 * Math.pow(bl, 1 / 2.4) - 0.055 : 12.92 * bl;
    const clamp = (v) => Math.round(Math.min(Math.max(0, v), 1) * 255);
    return [clamp(r), clamp(g), clamp(bl)];
}

/** Wizard step names as the user knows them (the API keeps the raw keys). */
const FUNNEL_STEP_LABELS = {
    manual_selection: "Manual selection",
    width_filter: "Line width filter",
    time_filter: "Marking time filter",
    chromatic_filter: "Chromaticity filter",
    line_count_filter: "Line count filter",
    composition: "Hue wheel selection",
    spatial_filter: "Spatial Repeatability Filter",
    mode_contrast_filter: "Mode Contrast Filter",
    image_primaries: "Primaries",
    width_group_choice: "Width Group Choice",
};
const FUNNEL_STATUS_LABELS = {
    COMPLETED: "Completed",
    NOT_DONE: "Not applied",
    SKIPPED: "Skipped",
    PENDING: "Pending",
};
const funnelStepLabel = (step) => FUNNEL_STEP_LABELS[step]
    || step.split("_").map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
const funnelStatusLabel = (status) => FUNNEL_STATUS_LABELS[status]
    || (status ? status.charAt(0) + status.slice(1).toLowerCase().replace(/_/g, " ") : "—");

const fmt = (value, digits = 2) =>
    (value === null || value === undefined) ? "—" : Number(value).toFixed(digits);

function StatCard({ title, stats, digits = 2, unit = "" }) {
    return (
        <Card variant="outlined" sx={{ minWidth: 220, borderRadius: 3, borderColor: "rgba(15, 23, 42, 0.08)" }}>
            <CardContent>
                <Typography variant="subtitle2" color="text.secondary">{title}</Typography>
                {stats ? (
                    <Typography variant="body2">
                        mean {fmt(stats.mean, digits)}{unit} · median {fmt(stats.median, digits)}{unit}<br />
                        p95 {fmt(stats.p95, digits)}{unit} · max {fmt(stats.max, digits)}{unit}
                    </Typography>
                ) : (
                    <Typography variant="body2" color="text.secondary">not measured</Typography>
                )}
            </CardContent>
        </Card>
    );
}

// Step 6: data and overview of the finished selection — funnel, repeatability
// ΔE00 statistics, gamut and hue plots, and the exportable logo palette grid.
export default function ColorSelectionOverview() {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [errorMessage, setErrorMessage] = useState(null);
    const [overview, setOverview] = useState(null);
    const [paletteDownloadBusy, setPaletteDownloadBusy] = useState(false);
    const [paletteMarkingBusy, setPaletteMarkingBusy] = useState(false);
    /** Palette marking file: patch size in mm (as typed) and the crosses. */
    const [patchSize, setPatchSize] = useState("");
    const [markCrosses, setMarkCrosses] = useState(true);
    const [paletteMarkingError, setPaletteMarkingError] = useState(null);
    /** Palette grid (rows, columns) as typed; stored on the server once
     * valid (api/selection/palette/grid), the automatic grid otherwise. */
    const [gridRows, setGridRows] = useState("");
    const [gridColumns, setGridColumns] = useState("");
    const [gridError, setGridError] = useState(null);
    /** Palette position on the plate ({x, y} mm from its corner; null until
     * the placement preview reports the default) and that preview. */
    const [palettePosition, setPalettePosition] = useState(null);
    const [paletteLayout, setPaletteLayout] = useState(null);
    /** Measured repeatability (all capture modes), when the test was run. */
    const [repeatability, setRepeatability] = useState(null);
    const [paintMode, setPaintMode] = useState(1);
    /** Uploaded repeatability photos (small PNGs) for the filter section. */
    const [photos, setPhotos] = useState(null);
    const [openPhoto, setOpenPhoto] = useState(null);
    /** Mode contrast selection: the applied Mode Contrast Filter (groups + pair map). */
    const [modeContrast, setModeContrast] = useState(null);
    /** Overview tabs (like the exploration summary page). A deep link
     * (``location.state.tab`` / ``sampleId``, e.g. from a reproduction's
     * gradient colors) opens the gamut tab on that palette colour. */
    const location = useLocation();
    const deepLink = location?.state || {};
    const [activeTab, setActiveTab] = useState(deepLink.tab || "summary");
    const [highlightedSampleId, setHighlightedSampleId] = useState(
        deepLink.sampleId ? String(deepLink.sampleId) : null,
    );
    /** Measurement drift tab: mode shown, excluded patches, inspected sample. */
    const [driftMode, setDriftMode] = useState(1);
    const [driftShowExcluded, setDriftShowExcluded] = useState(false);
    const [origin, setOrigin] = useState(null);
    const [originLoading, setOriginLoading] = useState(false);
    const [originError, setOriginError] = useState(null);
    /** Gamut tab: the inspected palette colour (same endpoint, no plate part). */
    const [gamutOrigin, setGamutOrigin] = useState(null);
    const [gamutOriginLoading, setGamutOriginLoading] = useState(false);
    const [gamutOriginError, setGamutOriginError] = useState(null);

    const fetchOrigin = async (sampleId, setters) => {
        const [setValue, setLoading, setError] = setters;
        setLoading(true);
        setError(null);
        try {
            const response = await axios.post("api/selection/sample/origin", { selectionId, sampleId });
            setValue(response.data.origin);
        } catch (error) {
            console.error("Error loading the sample origin:", error);
            setValue(null);
            setError(error?.response?.data?.error || "Loading the sample failed.");
        } finally {
            setLoading(false);
        }
    };
    const inspectSample = (sampleId) => fetchOrigin(sampleId, [setOrigin, setOriginLoading, setOriginError]);
    const inspectGamutSample = (sampleId) => fetchOrigin(sampleId, [setGamutOrigin, setGamutOriginLoading, setGamutOriginError]);

    const { selectionId, kindKey, stepRoute } = useSelectionParams();

    useEffect(() => {
        const load = async () => {
            try {
                const response = await axios.post("api/selection/overview", { selectionId });
                setOverview(response.data.overview);
                const defaultPatchSize = response.data.overview?.palette_marking?.patch_size;
                if (defaultPatchSize != null) setPatchSize(String(defaultPatchSize));
                const storedGrid = response.data.overview?.palette_marking?.grid;
                if (Array.isArray(storedGrid)) {
                    setGridRows(String(storedGrid[0]));
                    setGridColumns(String(storedGrid[1]));
                }
                // A link may name the selection under another kind than its
                // own; the URL (and with it the side menu) follows the row.
                const openedType = response.data.overview?.selection?.selection_type;
                if (openedType && selectionKindKey(openedType) !== kindKey) {
                    navigate(stepRoute("overview", openedType), { replace: true, state: location?.state });
                }
                if (deepLink.sampleId) inspectGamutSample(String(deepLink.sampleId));
                if (response.data.overview?.mode_contrast) {
                    axios.post("api/selection/modecontrast/results", { selectionId })
                        .then((r) => setModeContrast(r.data.modeContrast || null))
                        .catch((e) => console.error("Error loading the mode contrast groups:", e));
                }
                if (response.data.overview?.selection?.repeatability) {
                    const results = await axios.post("api/selection/spatial/results", { selectionId });
                    setRepeatability(results.data.repeatability || null);
                    axios.post("api/selection/spatial/photos", { selectionId }, { timeout: 600000 })
                        .then((r) => setPhotos(r.data))
                        .catch((e) => console.error("Error loading repeatability photos:", e));
                }
            } catch (error) {
                console.error("Error loading overview:", error);
                setErrorMessage("Loading the selection overview failed.");
            } finally {
                setLoading(false);
            }
        };
        load();
    }, []);

    const downloadPalette = async () => {
        setPaletteDownloadBusy(true);
        try {
            const response = await axios.post(
                "api/selection/palette/export",
                { selectionId },
                { responseType: "blob", timeout: 600000 },
            );
            downloadBlob(response.data, "palette.zip");
        } catch (error) {
            console.error("Error downloading palette:", error);
        } finally {
            setPaletteDownloadBusy(false);
        }
    };

    // Blob-typed requests deliver error bodies as a Blob — decode the
    // backend's {error} detail if there is one.
    const requestErrorDetail = async (error) => {
        const data = error?.response?.data;
        if (data instanceof Blob) {
            try {
                return JSON.parse(await data.text()).error || null;
            } catch (parseError) {
                return null;
            }
        }
        return data?.error || null;
    };

    /** Largest patch size that fits the plate at the palette's position (the
     * placement preview's; until it answers, the overview's at the corner).
     * null: no limit known. */
    const maxPatchSize = paletteLayout
        ? paletteLayout.max_patch_size
        : overview?.palette_marking?.max_patch_size ?? null;
    const patchSizeValue = Number(patchSize);
    const patchSizePositive = String(patchSize).trim() !== ""
        && Number.isFinite(patchSizeValue) && patchSizeValue > 0;
    const patchSizeValid = patchSizePositive
        && (maxPatchSize === null || patchSizeValue <= maxPatchSize);
    const paletteFits = !paletteLayout || paletteLayout.fits;

    /** The palette grid: chosen here for a grid palette (a mode contrast
     * palette is marked as groups and has none). The typed rows x columns
     * must be whole numbers with room for every patch; the server refuses
     * anything else, so nothing is adjusted on this side either. */
    const paletteMarking = overview?.palette_marking;
    const gridChoosable = paletteMarking?.layout === "grid" && Array.isArray(paletteMarking?.automatic_grid);
    const sampleCount = paletteMarking?.sample_count ?? 0;
    const wholeNumber = (text) => String(text).trim() !== "" && Number.isInteger(Number(text)) && Number(text) >= 1;
    const gridRowsValue = Number(gridRows);
    const gridColumnsValue = Number(gridColumns);
    const gridValid = !gridChoosable
        || (wholeNumber(gridRows) && wholeNumber(gridColumns) && gridRowsValue * gridColumnsValue >= sampleCount);
    const gridIsAutomatic = paletteMarking?.grid_override == null;

    const savePaletteGrid = async (grid) => {
        setGridError(null);
        try {
            const response = await axios.post("api/selection/palette/grid", { selectionId, grid });
            const settings = response.data.palette_marking;
            setOverview((previous) => (previous ? { ...previous, palette_marking: settings } : previous));
            if (grid === null && Array.isArray(settings?.grid)) {
                setGridRows(String(settings.grid[0]));
                setGridColumns(String(settings.grid[1]));
            }
        } catch (error) {
            console.error("Error storing the palette grid:", error);
            setGridError(error?.response?.data?.error || "Storing the palette grid failed.");
        }
    };

    // A valid typed grid is stored once it rests for a moment; the placement
    // preview and the palette drawing then re-read it from the server.
    useEffect(() => {
        if (!gridChoosable || !gridValid) return undefined;
        const current = paletteMarking.grid;
        if (current && current[0] === gridRowsValue && current[1] === gridColumnsValue) return undefined;
        const timer = setTimeout(() => savePaletteGrid([gridRowsValue, gridColumnsValue]), 300);
        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [gridRows, gridColumns, gridChoosable]);

    const gridHelpText = (() => {
        if (!gridChoosable) return "";
        if (gridError) return gridError;
        if (!gridValid) return `${sampleCount} patches need at least ${sampleCount} cells.`;
        const automatic = paletteMarking.automatic_grid;
        return `${sampleCount} patches; automatic ${automatic[0]} × ${automatic[1]}.`;
    })();

    const requestPaletteMarking = async () => {
        try {
            const response = await axios.post(
                "api/selection/palette/marking/file",
                {
                    selectionId, patchSize: patchSizeValue, markCrosses,
                    ...(palettePosition ? { position: palettePosition } : {}),
                },
                { responseType: "blob", timeout: 600000 },
            );
            return response.data;
        } catch (error) {
            console.error("Error generating the palette marking file:", error);
            throw new Error((await requestErrorDetail(error)) || "Generating the marking file failed.");
        }
    };

    const downloadPaletteMarking = async () => {
        setPaletteMarkingBusy(true);
        setPaletteMarkingError(null);
        try {
            downloadBlob(await requestPaletteMarking(), "palette_marking.json");
        } catch (error) {
            setPaletteMarkingError(error.message);
        } finally {
            setPaletteMarkingBusy(false);
        }
    };

    const getPaletteMarkingBlobForLaser = async () => {
        if (!patchSizeValid) throw new Error("Invalid patch size.");
        if (!gridValid) throw new Error("Invalid palette grid.");
        if (!paletteFits) throw new Error("The palette does not fit on the plate.");
        return {
            blob: await requestPaletteMarking(),
            filename: "palette_marking.json",
        };
    };

    // Memoised on the overview so that a re-render for other state (the origin
    // card loading after a click, a tab switch) hands the 3D gamut plot the same
    // points object and Plotly is not asked to re-plot — a re-plot would reset
    // the user's rotation/zoom.
    const gamut3d = useMemo(() => {
        const kindLabel = { chromatic: "Chromatic", black: "Black", white: "White" };
        return (overview?.samples || []).map((s) => ({
            lab: [s.l_star, s.a_star, s.b_star],
            rgb: labToRgb255(s.l_star, s.a_star, s.b_star),
            label: kindLabel[s.kind] || s.kind,
            sublabel: [
                s.width_mm != null ? `${(s.width_mm * 1000).toFixed(1)} µm` : null,
                s.spatial_de00 != null ? `ΔE00 ${Number(s.spatial_de00).toFixed(2)}` : null,
            ].filter(Boolean).join(" · "),
            sample_id: s.id,
        }));
    }, [overview]);

    /** Patch colours of the placement preview, from the palette's L*a*b*. */
    const patchColors = useMemo(() => Object.fromEntries((overview?.samples || []).map((s) => [
        String(s.id), `rgb(${labToRgb255(s.l_star, s.a_star, s.b_star).join(",")})`,
    ])), [overview]);

    if (loading) {
        return <LoadingComponent message="Loading the selection overview..." />;
    }
    if (errorMessage || !overview) {
        return (
            <Stack alignItems="center" marginTop="4%">
                <Alert severity="error" sx={{ width: "85ch" }}>{errorMessage || "No data."}</Alert>
            </Stack>
        );
    }

    const { selection, funnel, samples, stats } = overview;
    /** Hover card entry per sample id, colored from its L*a*b*. */
    const paletteEntries = Object.fromEntries(samples.map((s) => [
        s.id, overviewSampleEntry(s, `rgb(${labToRgb255(s.l_star, s.a_star, s.b_star).join(",")})`),
    ]));
    const modeContrastSummary = overview.mode_contrast || null;
    const modeContrastType = isModeContrastSelection(selection.selection_type);
    const listRoute = selectionKind(selection.selection_type).listRoute;
    const tabDefinitions = [
        { key: "summary", label: "Summary" },
        { key: "gamut", label: "Gamut" },
        ...(selection.repeatability ? [
            { key: "filter", label: "Spatial Repeatability Filter" },
            { key: "drift", label: "Measurement Drift" },
        ] : []),
        ...(modeContrastSummary ? [{ key: "modecontrast", label: "Mode Contrast Filter" }] : []),
        { key: "palette", label: "Palette" },
    ];
    const presentation = selectionStatusPresentation(selection.status);
    const kind = selectionKind(selection.selection_type);
    // A manual selection has no repeatability step at all.
    const repeatabilityNotApplicable = selection.spatial_repeatability === "not_applicable";
    const repeatabilitySkipped = selection.spatial_repeatability === "skipped";

    const gamutData = samples.map((s) => ({
        a_value: s.a_star,
        b_value: s.b_star,
        color: labToRgb255(s.l_star, s.a_star, s.b_star),
    }));
    const hueData = samples
        .filter((s) => s.kind === "chromatic")
        .map((s) => ({
            hue: s.hue_deg,
            chroma: s.chroma,
            color: labToRgb255(s.l_star, s.a_star, s.b_star),
        }));

    const de00Values = samples.map((s) => s.spatial_de00).filter((v) => v !== null && v !== undefined);
    const de00Bins = [];
    if (de00Values.length > 0) {
        const maxDe = Math.max(...de00Values, 1);
        const binCount = 12;
        const binWidth = maxDe / binCount;
        for (let i = 0; i < binCount; i += 1) {
            de00Bins.push({
                bin: `${(i * binWidth).toFixed(1)}`,
                count: de00Values.filter((v) => v >= i * binWidth && v < (i + 1) * binWidth + (i === binCount - 1 ? 1e-6 : 0)).length,
            });
        }
    }

    return (
        <>
        <Stack alignItems="center" sx={{ mt: "2%", mb: 4, width: "100%" }} spacing={2}>
            <ExplorationPageShell
                title={selection.name}
                subtitle={`${selection.laser || "?"} on ${selection.material || "?"} · Explorations: ${selection.explorations.join(", ") || "—"}${selection.prepass_sample_id ? ` · Prepass: ${selection.prepass_sample_id}` : ""}`}
                stepSidebar={selectionSidebar("overview", stats.total, selection.selection_type, overview.image_iterations)}
                headerBadges={(
                    <>
                        <Chip
                            size="small"
                            label={presentation.label}
                            sx={{ bgcolor: presentation.color, color: "#fff", fontWeight: 600 }}
                        />
                        <Chip
                            size="small"
                            variant="outlined"
                            label={kind.typeLabel}
                            sx={{ fontWeight: 600, bgcolor: "#fff" }}
                        />
                        {selection.grid_size_spatial ? (
                            <Chip
                                size="small"
                                label={
                                    repeatabilityNotApplicable
                                        ? `Grid ${selection.grid_size_spatial[0]} x ${selection.grid_size_spatial[1]}`
                                        : (selection.batches?.length ?? 0) > 1
                                        ? `${selection.batches.length} batches · up to ${selection.grid_size_spatial[0]} x ${selection.grid_size_spatial[1]} · ${selection.num_spatial_repeat} repetitions`
                                        : `Grid ${selection.grid_size_spatial[0]} x ${selection.grid_size_spatial[1]} · ${selection.num_spatial_repeat} repetitions`
                                }
                            />
                        ) : null}
                        {repeatabilitySkipped ? (
                            <Chip
                                size="small"
                                color="warning"
                                variant="outlined"
                                label="Spatial Repeatability Filter skipped"
                                sx={{ fontWeight: 600 }}
                            />
                        ) : null}
                    </>
                )}
                footerBelowPaper={
                    <Box sx={{ width: "100%" }}>
                        <NavBarBottom
                            variant="embedded"
                            hideReturnButton
                            nextLabel="Done"
                            prevPage={listRoute}
                            nextPage={listRoute}
                            enableNextPage={false}
                            nextPageClickFunc={(page) => navigate(page)}
                        />
                    </Box>
                }
            >
                <Stack spacing={4}>
                    <Tabs
                        value={tabDefinitions.findIndex((t) => t.key === activeTab)}
                        onChange={(_e, v) => setActiveTab(tabDefinitions[v].key)}
                        variant="fullWidth"
                        sx={{
                            borderBottom: 1,
                            borderColor: "divider",
                            "& .MuiTab-root": { textTransform: "none", fontWeight: 600, fontSize: "0.9rem" },
                        }}
                        aria-label="Selection overview"
                    >
                        {tabDefinitions.map((tab) => (
                            <Tab key={tab.key} label={tab.label} />
                        ))}
                    </Tabs>

                    {activeTab === "summary" && (<>
                    {/* Composition + performance stats */}
                    <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap justifyContent="flex-start">
                        <Card variant="outlined" sx={{ minWidth: 220, borderRadius: 3, borderColor: "rgba(15, 23, 42, 0.08)" }}>
                            <CardContent>
                                <Typography variant="subtitle2" color="text.secondary">Palette</Typography>
                                <Typography variant="body2">
                                    {`${stats.total} samples`}<br />
                                    {`${stats.chromatic} chromatic · ${stats.black} black · ${stats.white} white`}
                                </Typography>
                            </CardContent>
                        </Card>
                        {modeContrastSummary && (
                            <Card variant="outlined" sx={{ minWidth: 220, borderRadius: 3, borderColor: "rgba(15, 23, 42, 0.08)" }}>
                                <CardContent>
                                    <Typography variant="subtitle2" color="text.secondary">Mode contrast pairs</Typography>
                                    <Typography variant="body2">
                                        {modeContrastSummary.directions.map((d) => (
                                            <React.Fragment key={directionKey(d)}>
                                                {`${directionLabel(d)}: ${selectedText(d, d.groups, d.samples)}`}
                                                <br />
                                            </React.Fragment>
                                        ))}
                                    </Typography>
                                </CardContent>
                            </Card>
                        )}
                        {repeatabilityNotApplicable ? null : (
                            <StatCard title="Spatial repeatability ΔE00" stats={stats.de00} />
                        )}
                        <StatCard title="Line width (mm)" stats={stats.width_mm} digits={3} />
                        <StatCard title="Marking time / unit cluster (s)" stats={stats.time_per_unit_cluster} digits={3} />
                        <StatCard title="Chroma C*" stats={stats.chroma} digits={1} />
                    </Stack>

                    {/* Funnel */}
                    <Box>
                        <Typography variant="h6" gutterBottom>Selection funnel</Typography>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Step</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell align="right">Samples in</TableCell>
                                    <TableCell align="right">Samples out</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {funnel.map((row) => (
                                    <TableRow key={row.step}>
                                        <TableCell>{funnelStepLabel(row.step)}</TableCell>
                                        <TableCell sx={{ color: row.status === "COMPLETED" ? "success.main" : "text.secondary" }}>
                                            {funnelStatusLabel(row.status)}
                                        </TableCell>
                                        <TableCell align="right">{row.input_count}</TableCell>
                                        <TableCell align="right">{row.status === "COMPLETED" ? row.output_count : "—"}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </Box>

                    </>)}

                    {activeTab === "gamut" && (<>
                    <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Final gamut (CIELab)</Typography>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                            Every palette colour at its L*, a*, b* in its own colour. Drag to rotate, scroll to zoom; click a colour to inspect the sample.
                        </Typography>
                        <Stack direction={{ xs: "column", lg: "row" }} spacing={2} alignItems="flex-start">
                            <Box sx={{ flex: 1, minWidth: 0, width: "100%", maxWidth: 900, border: "1px solid", borderColor: "divider", borderRadius: 2, overflow: "hidden" }}>
                                <LabGamutPlot
                                    points={gamut3d}
                                    height={480}
                                    selectedSampleId={highlightedSampleId}
                                    onSelect={(point) => { setHighlightedSampleId(String(point.sample_id)); inspectGamutSample(point.sample_id); }}
                                />
                            </Box>
                            <Box sx={{ width: { xs: "100%", lg: 520 }, flexShrink: 0 }}>
                                <SampleOriginCard
                                    origin={gamutOrigin}
                                    loading={gamutOriginLoading}
                                    error={gamutOriginError}
                                    showPlate={false}
                                    emptyText="Click a colour in the plot to see where the sample came from and its design and performance space."
                                />
                            </Box>
                        </Stack>
                    </Box>
                    <Stack direction="row" spacing={4} flexWrap="wrap" useFlexGap justifyContent="flex-start">
                        <Box>
                            <Typography variant="subtitle1" align="center" sx={{ fontWeight: 600 }}>
                                Final gamut (a*/b*)
                            </Typography>
                            <MyScatterChart dataColor={gamutData} width={400} height={330} />
                        </Box>
                        <Box>
                            <Typography variant="subtitle1" align="center" sx={{ fontWeight: 600 }}>
                                Hue distribution (chromatic samples)
                            </Typography>
                            <ScatterChart width={400} height={330} margin={{ top: 12, right: 16, left: 12, bottom: 28 }}>
                                <XAxis
                                    type="number"
                                    dataKey="hue"
                                    name="Hue"
                                    domain={[0, 360]}
                                    ticks={[0, 90, 180, 270, 360]}
                                    label={{ value: "Hue (°)", position: "insideBottom", offset: -12 }}
                                />
                                <YAxis
                                    type="number"
                                    dataKey="chroma"
                                    name="Chroma"
                                    label={{ value: "C*", angle: -90, position: "center" }}
                                />
                                <CartesianGrid strokeDasharray="3 3" />
                                <Tooltip />
                                <Scatter data={hueData} isAnimationActive={false}>
                                    {hueData.map((entry, index) => (
                                        <Cell key={`hue-${index}`} fill={`rgb(${entry.color.join(',')})`} />
                                    ))}
                                </Scatter>
                            </ScatterChart>
                        </Box>
                    </Stack>

                    </>)}

                    {/* Repeatability: how the palette was measured and what was struck out */}
                    {activeTab === "filter" && selection.repeatability && (
                        <Box>
                            <Typography variant="h6" gutterBottom>Spatial Repeatability Filter</Typography>
                            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
                                <Chip size="small" label={`${selection.repeatability.modes} capture mode${selection.repeatability.modes > 1 ? "s" : ""}`} />
                                {selection.repeatability.limits.map((limit, i) => (
                                    <Chip key={i} size="small" variant="outlined" label={`${modeLabel(i + 1)}: ΔE00 limit ${limit}`} />
                                ))}
                                <Chip size="small" label={`${selection.repeatability.total} patches marked`} />
                                <Chip size="small" color="success" variant="outlined" label={`${selection.repeatability.repeatable} within the limit`} />
                                {selection.repeatability.modes > 1 && Object.entries(selection.repeatability.repeatable_per_mode).map(([mode, n]) => (
                                    <Chip key={mode} size="small" variant="outlined" label={`${modeLabel(mode)}: ${n} pass`} />
                                ))}
                                <Chip size="small" color="error" variant="outlined" label={`${selection.repeatability.manual_rejected} excluded manually`} />
                            </Stack>
                            {de00Bins.length > 0 && (
                                <Box sx={{ mb: 2 }}>
                                <Typography variant="subtitle1" align="center" sx={{ fontWeight: 600 }}>
                                    ΔE00 repeatability histogram
                                </Typography>
                                <BarChart width={400} height={330} data={de00Bins} margin={{ top: 12, right: 16, left: 12, bottom: 28 }}>
                                    <XAxis dataKey="bin" label={{ value: "ΔE00", position: "insideBottom", offset: -12 }} />
                                    <YAxis allowDecimals={false} label={{ value: "Samples", angle: -90, position: "center" }} />
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <Tooltip />
                                    <Bar dataKey="count" fill="#64748b" isAnimationActive={false} />
                                </BarChart>
                            </Box>
                        )}
                            {repeatability && (
                                <Stack spacing={2}>
                                    <Typography variant="body2" color="text.secondary" sx={{ maxWidth: "90ch" }}>
                                        {repeatability.modes > 1
                                            ? "Measured gamut as read from the plate: a sphere at the mode 1 reading, a diamond at the mode 2 reading, joined by a line; rejected patches as small crosses. Drag to rotate, scroll to zoom."
                                            : "Measured gamut as read from the plate; excluded patches as small crosses. Drag to rotate, scroll to zoom."}
                                    </Typography>
                                    <Box sx={{ width: "100%", maxWidth: 900, border: "1px solid", borderColor: "divider", borderRadius: 2, overflow: "hidden" }}>
                                        <RepeatabilityGamutPlot
                                            cells={repeatability.cells}
                                            modes={repeatability.modes}
                                            limits={repeatability.limits}
                                            showRejected
                                            height={480}
                                        />
                                    </Box>
                                    <RepeatabilityGrid
                                        repeatability={repeatability}
                                        paintMode={paintMode}
                                        onPaintModeChange={setPaintMode}
                                        readOnly
                                    />
                                    <RepeatabilityPhotoStrip photos={photos} onOpen={setOpenPhoto} selectionId={selectionId} />
                                </Stack>
                            )}
                        </Box>
                    )}

                    {/* Measurement drift: exploration value -> plate reading, per sample */}
                    {activeTab === "drift" && repeatability && (
                        <Box>
                            <Typography variant="h6" gutterBottom>Measurement Drift</Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, maxWidth: "90ch" }}>
                                Each retained colour as a line in CIELab from the value it was selected with
                                (the exploration&apos;s measurement, small grey marker) to its reading on the
                                repeatability plate (sphere in the measured colour). The length of the line is
                                the drift ΔE00. Click a sphere or a patch on the grid to inspect the sample.
                            </Typography>
                            <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }} flexWrap="wrap" useFlexGap>
                                {repeatability.modes > 1 && (
                                    <ToggleButtonGroup
                                        size="small" exclusive value={driftMode}
                                        onChange={(_e, v) => v && setDriftMode(v)}
                                        aria-label="Capture mode shown"
                                    >
                                        {Array.from({ length: repeatability.modes }, (_, i) => i + 1).map((mode) => (
                                            <ToggleButton key={mode} value={mode} sx={{ textTransform: "none", px: 1.5 }}>{modeLabel(mode)}</ToggleButton>
                                        ))}
                                    </ToggleButtonGroup>
                                )}
                                <FormControlLabel
                                    control={<Switch size="small" checked={driftShowExcluded} onChange={(e) => setDriftShowExcluded(e.target.checked)} />}
                                    label={<Typography variant="body2">Show excluded patches</Typography>}
                                />
                            </Stack>
                            <Stack direction={{ xs: "column", lg: "row" }} spacing={2} alignItems="flex-start">
                                <Box sx={{ flex: 1, minWidth: 0, width: "100%", maxWidth: 900, border: "1px solid", borderColor: "divider", borderRadius: 2, overflow: "hidden" }}>
                                    <MeasurementDriftPlot
                                        cells={repeatability.cells}
                                        limits={repeatability.limits}
                                        mode={driftMode}
                                        showExcluded={driftShowExcluded}
                                        onSelect={(cell) => inspectSample(cell.sample_id)}
                                        height={520}
                                    />
                                </Box>
                                <Box sx={{ width: { xs: "100%", lg: 520 }, flexShrink: 0 }}>
                                    <SampleOriginCard origin={origin} loading={originLoading} error={originError} />
                                </Box>
                            </Stack>
                            <Box sx={{ mt: 2 }}>
                                <RepeatabilityGrid
                                    repeatability={repeatability}
                                    paintMode={driftMode}
                                    onPaintModeChange={setDriftMode}
                                    onInspect={(sampleId) => inspectSample(sampleId)}
                                    readOnly
                                />
                            </Box>
                        </Box>
                    )}

                    {/* Mode contrast selection: the applied Mode Contrast Filter */}
                    {activeTab === "modecontrast" && modeContrastSummary && (
                        <Box>
                            <Typography variant="h6" gutterBottom>Mode Contrast Filter</Typography>
                            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
                                {modeContrastSummary.directions.map((d) => (
                                    <Chip
                                        key={directionKey(d)}
                                        size="small"
                                        variant="outlined"
                                        label={`${directionLabel(d)}: ${directionLimitsText(d)} · ${d.kind === "stable" ? `${d.samples} of ${d.count} samples` : `${d.groups} of ${d.count} pairs`}`}
                                    />
                                ))}
                            </Stack>
                            {modeContrast ? (
                                <Stack spacing={3}>
                                    <Box>
                                        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Result per category</Typography>
                                        <ModeContrastResultTable directions={modeContrast.directions} />
                                    </Box>
                                    <Box>
                                        <Typography variant="subtitle2" fontWeight={700}>Pair map</Typography>
                                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                                            Every pair of palette samples at its ΔE00 in mode 1 and in mode 2, one pair category at a time (pick it above the map): shaded, the limits; large points, pairs inside a selected group.
                                        </Typography>
                                        <ModeContrastPairPlot pairs={modeContrast.pairs} directions={modeContrast.directions} />
                                    </Box>
                                    <ModeContrastGroups directions={modeContrast.directions} entries={paletteEntries} />
                                </Stack>
                            ) : (
                                <CircularProgress size={24} aria-label="Loading the groups" />
                            )}
                        </Box>
                    )}

                    {/* Palette: exactly the composed samples, nothing more */}
                    {activeTab === "palette" && (
                    <Box>
                        <Typography variant="h6" gutterBottom>Palette</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: "90ch" }}>
                            {repeatabilityNotApplicable
                                ? `The palette contains the ${stats.total} manually picked samples of this selection — the color set used for logo reproduction.`
                                : repeatabilitySkipped
                                ? `The palette contains the ${stats.total} composed samples of this selection — the color set used for logo reproduction. The spatial repeatability was not measured.`
                                : modeContrastType && modeContrastSummary
                                    ? `The palette contains the ${stats.total} samples of the mode contrast groups.`
                                    : `The palette contains the ${stats.total} composed and verified samples of this selection — the color set used for logo reproduction.`}
                        </Typography>

                        {/* The palette in the shape it is marked: the placement
                            preview's layout (the marking file's computation).
                            Until it is known, the colours in palette order. */}
                        {paletteLayout ? (
                            <Box sx={{ mb: 2 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                                    {`${samples.length} colors`}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                                    {paletteShapeText(paletteLayout)}
                                </Typography>
                                <PaletteAsMarked layout={paletteLayout} colors={patchColors} entries={paletteEntries} />
                            </Box>
                        ) : null}
                        {modeContrastType && modeContrast ? (
                            <Box sx={{ mb: 2 }}>
                                <ModeContrastGroups directions={modeContrast.directions} entries={paletteEntries} />
                            </Box>
                        ) : paletteLayout ? null : (() => {
                            const kindRank = { chromatic: 0, black: 1, white: 2 };
                            const swatches = [...samples].sort((a, b) => {
                                if (kindRank[a.kind] !== kindRank[b.kind]) {
                                    return kindRank[a.kind] - kindRank[b.kind];
                                }
                                if (a.kind === "chromatic") return a.hue_deg - b.hue_deg;
                                return a.kind === "black" ? a.l_star - b.l_star : b.l_star - a.l_star;
                            });
                            return (
                                <Box sx={{ mb: 2 }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                                        {`${swatches.length} colors`}
                                    </Typography>
                                    <Stack direction="row" flexWrap="wrap" useFlexGap spacing={0.5}>
                                        {swatches.map((s) => (
                                            <MuiTooltip
                                                key={s.id}
                                                placement="top"
                                                title={<PaletteSampleTooltip entry={paletteEntries[s.id]} />}
                                            >
                                                <Box
                                                    data-testid="palette-swatch"
                                                    sx={{
                                                        width: 30,
                                                        height: 30,
                                                        borderRadius: 0.75,
                                                        bgcolor: paletteEntries[s.id].hex,
                                                        border: "1px solid rgba(15, 23, 42, 0.2)",
                                                    }}
                                                />
                                            </MuiTooltip>
                                        ))}
                                    </Stack>
                                </Box>
                            );
                        })()}

                        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
                            <Button
                                variant="outlined"
                                startIcon={<DownloadIcon />}
                                onClick={downloadPalette}
                                disabled={paletteDownloadBusy}
                                sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2 }}
                            >
                                {paletteDownloadBusy
                                    ? "Preparing download..."
                                    : "Download palette"}
                            </Button>
                        </Stack>

                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 3, mb: 1.5 }}>
                            Marking file
                        </Typography>
                        <Stack direction="row" spacing={2} alignItems="flex-start" flexWrap="wrap" useFlexGap>
                            <TextField
                                id="palette-patch-size"
                                label="Patch size (mm)"
                                type="number"
                                size="small"
                                value={patchSize}
                                onChange={(event) => setPatchSize(event.target.value)}
                                error={!patchSizeValid}
                                helperText={maxPatchSize !== null ? `Max ${maxPatchSize} mm.` : " "}
                                inputProps={{
                                    min: 0.1,
                                    step: 0.1,
                                    ...(maxPatchSize !== null ? { max: maxPatchSize } : {}),
                                }}
                                sx={{ width: 180 }}
                            />
                            <FormControlLabel
                                control={(
                                    <Checkbox
                                        checked={markCrosses}
                                        onChange={(event) => setMarkCrosses(event.target.checked)}
                                    />
                                )}
                                label="Mark crosses"
                            />
                            {gridChoosable && (
                                <Box>
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <TextField
                                            id="palette-grid-rows"
                                            label="Rows"
                                            type="number"
                                            size="small"
                                            value={gridRows}
                                            onChange={(event) => { setGridRows(event.target.value); setGridError(null); }}
                                            error={!gridValid}
                                            inputProps={{ min: 1, step: 1 }}
                                            sx={{ width: 100 }}
                                        />
                                        <Typography variant="body2" color="text.secondary">×</Typography>
                                        <TextField
                                            id="palette-grid-columns"
                                            label="Columns"
                                            type="number"
                                            size="small"
                                            value={gridColumns}
                                            onChange={(event) => { setGridColumns(event.target.value); setGridError(null); }}
                                            error={!gridValid}
                                            inputProps={{ min: 1, step: 1 }}
                                            sx={{ width: 100 }}
                                        />
                                        <Button
                                            size="small"
                                            onClick={() => savePaletteGrid(null)}
                                            disabled={gridIsAutomatic}
                                            sx={{ textTransform: "none", fontWeight: 600 }}
                                        >
                                            Automatic
                                        </Button>
                                    </Stack>
                                    <Typography
                                        variant="caption"
                                        color={gridValid && !gridError ? "text.secondary" : "error"}
                                        sx={{ display: "block", mt: 0.5, ml: 1.75 }}
                                    >
                                        {gridHelpText}
                                    </Typography>
                                </Box>
                            )}
                        </Stack>
                        <Typography variant="body2" sx={{ fontWeight: 600, mt: 1, mb: 1 }}>
                            Position on the plate
                        </Typography>
                        <PalettePlacement
                            selectionId={selectionId}
                            patchSize={patchSizePositive ? patchSizeValue : null}
                            markCrosses={markCrosses}
                            position={palettePosition}
                            grid={gridChoosable ? paletteMarking.grid : null}
                            onPositionChange={setPalettePosition}
                            onLayoutChange={setPaletteLayout}
                            colors={patchColors}
                        />
                        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mt: 1.5 }}>
                            <Button
                                variant="outlined"
                                startIcon={<DownloadIcon />}
                                onClick={downloadPaletteMarking}
                                disabled={paletteMarkingBusy || !patchSizeValid || !gridValid || !paletteFits}
                                sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2 }}
                            >
                                {paletteMarkingBusy
                                    ? "Generating marking file..."
                                    : "Download palette marking file"}
                            </Button>
                            <MarkingTimeChip value={paletteLayout?.marking_time} />
                        </Stack>
                        {paletteMarkingError && (
                            <Alert
                                severity="error"
                                sx={{ mt: 1, maxWidth: 560 }}
                                onClose={() => setPaletteMarkingError(null)}
                            >
                                {paletteMarkingError}
                            </Alert>
                        )}
                        <Box sx={{ mt: 1 }}>
                            <LaserMarkPanel
                                getMarkingBlob={getPaletteMarkingBlobForLaser}
                                predictedMarkingTime={paletteLayout?.marking_time}
                            >
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                    Mark the palette as a grid directly on the laser connected to this computer:
                                </Typography>
                            </LaserMarkPanel>
                        </Box>
                    </Box>
                    )}
                </Stack>
            </ExplorationPageShell>
        </Stack>

            <Dialog open={Boolean(openPhoto)} onClose={() => setOpenPhoto(null)} maxWidth="lg">
                <DialogTitle>{openPhoto?.label}</DialogTitle>
                <DialogContent>
                    {openPhoto && (
                        <img src={openPhoto.src} alt={openPhoto.label} style={{ maxWidth: "100%", maxHeight: "75vh", display: "block" }} />
                    )}
                </DialogContent>
            </Dialog>
        </>
    );
}
